import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import toast from 'react-hot-toast';
import storeService from '../../../../services/storeService';
import {
  CreateStoreFormData,
  UpdateStoreFormData,
} from '../../types';
import { STORE_SUCCESS_MESSAGES, STORE_ERROR_MESSAGES } from '../../constants';

export const useCreateStoreMutation = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data: CreateStoreFormData) => storeService.createStore(data),
    onSuccess: (response) => {
      if (response.success) {
        toast.success(STORE_SUCCESS_MESSAGES.STORE_CREATED);
        queryClient.invalidateQueries({ queryKey: ['stores'] });
        queryClient.invalidateQueries({ queryKey: ['allStores'] });
        navigate('/stores');
      } else {
        toast.error(response.error || STORE_ERROR_MESSAGES.CREATE_STORE_FAILED);
      }
    },
    onError: (error) => {
      console.error('Create store error:', error);
      toast.error(STORE_ERROR_MESSAGES.CREATE_STORE_FAILED);
    },
  });
};

export const useUpdateStoreMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ storeId, data }: { storeId: string; data: UpdateStoreFormData }) =>
      storeService.updateStore(storeId, data),
    onSuccess: (response, { storeId }) => {
      console.log('response edit store === ', response);
      if (response.success) {
        toast.success(STORE_SUCCESS_MESSAGES.STORE_UPDATED);
        queryClient.invalidateQueries({ queryKey: ['stores'] });
        queryClient.invalidateQueries({ queryKey: ['allStores'] });
        queryClient.invalidateQueries({ queryKey: ['storeDetails', storeId] });
      } else {
        toast.error(response.error || STORE_ERROR_MESSAGES.UPDATE_STORE_FAILED);
      }
    },
    onError: (error) => {
      console.error('Update store error:', error);
      toast.error(STORE_ERROR_MESSAGES.UPDATE_STORE_FAILED);
    },
  });
};

export const useDeleteStoreMutation = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (storeId: string) => storeService.deleteStore(storeId),
    onSuccess: (response, storeId) => {
      if (response.success) {
        toast.success(STORE_SUCCESS_MESSAGES.STORE_DELETED);
        queryClient.invalidateQueries({ queryKey: ['stores'] });
        queryClient.invalidateQueries({ queryKey: ['allStores'] });
        queryClient.removeQueries({ queryKey: ['storeDetails', storeId] });
        navigate('/stores');
      } else {
        toast.error(response.error || STORE_ERROR_MESSAGES.DELETE_STORE_FAILED);
      }
    },
    onError: (error) => {
      console.error('Delete store error:', error);
      toast.error(STORE_ERROR_MESSAGES.DELETE_STORE_FAILED);
    },
  });
};

export const useUploadLogoMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ storeId, file }: { storeId: string; file: File }) => {
      const formData = new FormData();
      formData.append('file', file);
      return storeService.uploadStoreLogo(storeId, formData);
    },
    onSuccess: (response, { storeId }) => {
      if (response.success) {
        toast.success(STORE_SUCCESS_MESSAGES.LOGO_UPLOADED);
        queryClient.invalidateQueries({ queryKey: ['storeDetails', storeId] });
        queryClient.invalidateQueries({ queryKey: ['stores'] });
      } else {
        toast.error(response.error || STORE_ERROR_MESSAGES.UPLOAD_LOGO_FAILED);
      }
    },
    onError: (error) => {
      console.error('Upload logo error:', error);
      toast.error(STORE_ERROR_MESSAGES.UPLOAD_LOGO_FAILED);
    },
  });
};
